import React, { useState, useEffect } from "react";
import i18n from "../../i18n/config";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { BankAccount, GetBankAccountsResponse } from "../../types/api/odt";
import OnDemandTransferApi from "../../api/onDemandTransfer";
import {
  ACTION_DISPLAY_TYPES,
  BANK_ACCOUNT_CARD_TYPES,
  BANK_ACCOUNT_FLOW_TYPES,
  BANK_ACCOUNT_STATUS,
} from "../../constants/enums";
import { ROUTES } from "../../constants/clientRoutes";
import {
  UpdateBankAcccountParams,
  UpdateBankAccountFormFields,
} from "../../actions/bankAccounts";
import useWindowDimensions from "../../services/useWindowDimensions";
import { BREAKPOINTS } from "../../constants/breakPoints";

// types
import { RootState } from "../../types/redux";
import { BankAccountCardType } from "../../types/components";
import { BankAccountFlowType } from "../../types/forms";

// ui components
import FlowScreen from "../../components/Layouts/FlowScreen";
import Card from "../../components/Common/Card";
import BankAccountCard from "../../components/Common/BankAccountCard";
import Modal from "../../components/Common/Modal";
import Action from "../../components/Common/Action";
import DeleteBankAccountModal from "../../components/BankAccounts/DeleteBankAccountModal";
import DisbursementHistoryModal from "../../components/BankAccounts/DisbursementHistoryModal";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import useErrorModal from "../../hooks/useErrorModal";
import RowSkeletonGroup from "../../components/Common/Loaders/RowSkeletonGroup";

const BankAccounts = () => {
  const dispatch = useDispatch();
  const openErrorModal = useErrorModal();
  const history = useHistory();
  const { width } = useWindowDimensions();
  const { bankAccountDeleted } = useSelector((state: RootState) => state.bankAccounts);
  const [availableBanks, setAvailableBanks] = useState<BankAccount[]>();
  const [unavailableBanks, setUnavailableBanks] = useState<BankAccount[]>();
  const [loading, setLoading] = useState(true);
  const [showInfoTipModal, setShowInfoTipModal] = useState(false);
  const [infoTipModal, setInfoTipModal] = useState<{ title: string; content: string }>({
    title: "",
    content: "",
  });

  useEffect(() => {
    setLoading(true);
    dispatch(UpdateBankAccountFormFields([]));
    getBanks();
  }, [bankAccountDeleted]);

  const getBanks = async () => {
    try {
      const response = (await OnDemandTransferApi.GetBankAccounts()) as GetBankAccountsResponse;

      setAvailableBanks(
        response.bankAccounts.filter(
          (bank) => bank.bankAccountStatus === BANK_ACCOUNT_STATUS.AVAILABLE
        )
      );
      setUnavailableBanks(
        response.bankAccounts.filter(
          (bank) => bank.bankAccountStatus !== BANK_ACCOUNT_STATUS.AVAILABLE
        )
      );
    } catch (err) {
      openErrorModal(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInfoTipModal = (title: string, content: string) => {
    setShowInfoTipModal(!showInfoTipModal);
    setInfoTipModal({ title, content });
  };

  const handleAddBankAccount = () => {
    dispatch(UpdateBankAcccountParams(BANK_ACCOUNT_FLOW_TYPES.NEW as BankAccountFlowType));
    history.push(ROUTES.BANKS_ACCOUNTS.COUNTRY);
  };

  const isDesktop = width >= BREAKPOINTS.SM_MIN;

  return (
    <FlowScreen
      flowTitle={i18n.t("PAGES.BANK_ACCOUNTS.INDEX.TITLE")}
      className="v3"
      showBackButton={true}
      customBackButtonFunc={() => history.push(ROUTES.DASHBOARD)}
    >
      {loading && <RowSkeletonGroup numRows={5} />}

      {!loading && (
        <>
          <p className="mb-24 text-secondary">
            {i18n.t("PAGES.BANK_ACCOUNTS.INDEX.SUBTITLE")}
          </p>

          {/* Available banks */}
          <Card
            fullWidth
            bodyPadding="flush"
            className="mb-16"
            title={i18n.t("PAGES.BANK_ACCOUNTS.INDEX.AVAILABLE_HEADING")}
            data-cy="available-banks-section"
            actions={
              <Action
                title={isDesktop ? i18n.t("COMMON.ADD_BANK_ACCOUNT") : i18n.t("COMMON.ADD")}
                displayType={ACTION_DISPLAY_TYPES.PRIMARY}
                width={isDesktop ? null : "auto"}
                dataCy="add-bank-account-button"
                onClick={handleAddBankAccount}
              />
            }
          >
            {availableBanks?.map((bank) => (
              <BankAccountCard
                key={bank.allocationId}
                bank={bank}
                handleInfoTipModal={handleInfoTipModal}
                type={BANK_ACCOUNT_CARD_TYPES.BANK_ACCOUNT as BankAccountCardType}
              />
            ))}
          </Card>

          {/* Unavailable banks */}
          <Card
            fullWidth
            bodyPadding="flush"
            className="mb-16"
            title={i18n.t("PAGES.BANK_ACCOUNTS.INDEX.UNAVAILABLE_HEADING")}
            data-cy="unavailable-banks-section"
          >
            {unavailableBanks?.map((bank) => (
              <BankAccountCard
                key={bank.allocationId}
                bank={bank}
                handleInfoTipModal={handleInfoTipModal}
              />
            ))}
          </Card>

          <Modal
            onClose={() => setShowInfoTipModal(false)}
            open={showInfoTipModal}
            title={infoTipModal?.title}
            content={infoTipModal?.content}
          />

          <DeleteBankAccountModal />

          <DisbursementHistoryModal />

          <ApiErrorModal />
        </>
      )}
    </FlowScreen>
  );
};

export default BankAccounts;